import { useEffect, useState } from "react";
import { Receipt, Download, CheckCircle2, Wallet, Trash2, FileCheck2, FileCode2, BookOpen, Send, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PageHeader, StatusBadge, EmptyState, Field } from "../components/Shared";
import { api, eur, fmtDate, formatApiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canWrite } from "../lib/labels";

const safeDownload = async (url, params, name) => {
  try {
    await downloadFile(url, params, name);
  } catch (e) {
    toast.error("Téléchargement impossible");
  }
};

const EXPORT_FORMATS = {
  fec: "FEC (format officiel, tous logiciels)",
  sage: "Sage (import paramétrable .txt)",
  ebp: "EBP (import ASCII)",
  quadra: "Cegid Quadra (ASCII)",
  csv: "CSV universel",
  xlsx: "Excel (.xlsx)",
  pdf: "PDF (journal imprimable)",
};
const JOURNALS = { all: "Ventes + Banque", VT: "Journal des ventes", BQ: "Journal de banque (règlements)" };

const PickField = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="sm:w-64"><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`${testid}-${k}`}>{v}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

function AccountingExport() {
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: `${today.slice(0, 4)}-01-01`, end: today });
  const [fmt, setFmt] = useState("fec");
  const [journal, setJournal] = useState("all");
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="accounting-export">
      <div className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-teal-600" /><h3 className="text-base font-semibold text-slate-900">Export comptable des écritures</h3></div>
      <p className="mt-1 text-xs text-slate-500">Journal des ventes (411 / 706 / 44571) et de banque pour les règlements (512 / 411). FEC conforme à l'article A47 A-1 du LPF, formats d'import Sage, EBP, Cegid Quadra, ou Excel / PDF.</p>
      <div className="mt-4 flex flex-col flex-wrap gap-3 sm:flex-row sm:items-end">
        <Field label="Du"><Input type="date" data-testid="accounting-start-input" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} /></Field>
        <Field label="Au"><Input type="date" data-testid="accounting-end-input" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} /></Field>
        <PickField label="Format" value={fmt} onChange={setFmt} options={EXPORT_FORMATS} testid="accounting-format-select" />
        <PickField label="Journal" value={journal} onChange={setJournal} options={JOURNALS} testid="accounting-journal-select" />
        <Button data-testid="export-accounting-button" className="bg-[#0b2c6b]" onClick={() => safeDownload("/accounting/export", { ...range, format: fmt, journal }, `ecritures.${fmt === "csv" || fmt === "xlsx" || fmt === "pdf" ? fmt : "txt"}`)}><Download className="mr-2 h-4 w-4" />Télécharger</Button>
      </div>
    </div>
  );
}
import { INVOICE_STATUS } from "../lib/labels";

function exportCsv(invoices) {
  const rows = [["Numéro", "Client", "Émise", "Échéance", "HT", "TVA", "TTC", "Payé", "Statut"],
    ...invoices.map((i) => [i.number, i.client_name, i.issued_date, i.due_date, i.total_ht, i.tva, i.total_ttc, i.amount_paid, INVOICE_STATUS[i.status]?.label])];
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "factures-logidem.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function EInvoiceState({ inv, onChange }) {
  const e = inv.einvoice;
  const run = async (path, msg) => {
    try {
      await api.post(`/invoices/${inv.id}/${path}`);
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    onChange();
  };
  if (!e?.external_id) {
    return (
      <div className="mt-1 flex items-center gap-1">
        {e?.state === "error" && <span className="text-[11px] text-red-600" title={e.error} data-testid={`einvoice-error-${inv.id}`}>PA : erreur</span>}
        <button data-testid={`einvoice-send-${inv.id}`} onClick={() => run("einvoice", "Facture transmise à la Plateforme Agréée")} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1559d6] hover:underline"><Send className="h-3 w-3" />Transmettre à la PA</button>
      </div>
    );
  }
  return (
    <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-600" data-testid={`einvoice-state-${inv.id}`}>
      <span className="rounded bg-blue-50 px-1.5 py-0.5 font-semibold text-blue-700">PA {e.provider === "iopole" ? "Iopole" : "B2Brouter"} : {e.state}</span>
      <button title="Actualiser le statut" data-testid={`einvoice-refresh-${inv.id}`} onClick={() => run("einvoice/refresh", "Statut actualisé")}><RefreshCw className="h-3 w-3" /></button>
    </div>
  );
}

function PaymentDialog({ invoice, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  useEffect(() => {
    if (invoice) setAmount(String(invoice.amount_paid < invoice.deposit_amount ? invoice.deposit_amount - invoice.amount_paid : invoice.total_ttc - invoice.amount_paid));
  }, [invoice]);
  const save = async () => {
    try {
      await api.post(`/invoices/${invoice.id}/payment`, { amount: Number(amount) });
      toast.success("Règlement enregistré");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Enregistrer un règlement — {invoice?.number}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Reste dû : {eur((invoice?.total_ttc || 0) - (invoice?.amount_paid || 0))} · Acompte prévu : {eur(invoice?.deposit_amount)}</p>
        <Field label="Montant (€)"><Input data-testid="payment-amount-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <DialogFooter><Button data-testid="payment-save-button" className="bg-[#0b2c6b]" onClick={save}>Valider</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [paying, setPaying] = useState(null);
  const { user } = useAuth();
  const emailInvoice = async (inv) => {
    try {
      const { data } = await api.post(`/invoices/${inv.id}/email`);
      toast.success(`Facture envoyée à ${data.emailed_to}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => {
    load();
  }, []);

  const total = invoices.reduce((s, i) => s + i.total_ttc, 0);
  const paid = invoices.reduce((s, i) => s + i.amount_paid, 0);
  const late = invoices.filter((i) => i.status === "en_retard").length;

  const markPaid = async (inv) => {
    await api.put(`/invoices/${inv.id}`, { status: "payee" });
    toast.success("Facture soldée");
    load();
  };
  const remove = async (inv) => {
    if (!window.confirm(`Supprimer ${inv.number} ?`)) return;
    await api.delete(`/invoices/${inv.id}`);
    load();
  };

  return (
    <div className="fade-up">
      <PageHeader overline="Finance" title="Factures & règlements" subtitle="Créez une facture depuis un devis accepté (onglet Devis).">
        <Button variant="outline" data-testid="export-invoices-button" disabled={!invoices.length} onClick={() => exportCsv(invoices)}><Download className="mr-2 h-4 w-4" />Export CSV</Button>
      </PageHeader>
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        {[["Total facturé TTC", eur(total), "invoices-total"], ["Encaissé", eur(paid), "invoices-paid"], ["Factures en retard", late, "invoices-late"]].map(([l, v, t]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-5"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p><p className="mt-2 font-mono-num text-2xl font-bold text-[#0b2c6b]" data-testid={t}>{v}</p></div>
        ))}
      </div>
      {canWrite(user, "accounting") && <AccountingExport />}
      {invoices.length === 0 ? <EmptyState icon={Receipt} text="Aucune facture" testid="invoices-empty" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table data-testid="invoices-table">
            <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Échéance</TableHead><TableHead>Total TTC</TableHead><TableHead>Payé</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {invoices.map((i) => (
                <TableRow key={i.id} data-testid={`invoice-row-${i.id}`}>
                  <TableCell className="font-mono-num text-xs font-semibold">{i.number}<br /><span className="text-slate-400">{i.quote_number}</span></TableCell>
                  <TableCell className="font-medium">{i.client_name}</TableCell>
                  <TableCell className="text-sm">{fmtDate(i.due_date)}</TableCell>
                  <TableCell className="font-mono-num text-sm font-semibold">{eur(i.total_ttc)}</TableCell>
                  <TableCell className="font-mono-num text-sm">{eur(i.amount_paid)}</TableCell>
                  <TableCell><StatusBadge map={INVOICE_STATUS} value={i.status} testid={`invoice-status-${i.id}`} /><EInvoiceState inv={i} onChange={load} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button size="icon" variant="ghost" title="Facture électronique Factur-X (PDF)" data-testid={`facturx-invoice-${i.id}`} onClick={() => safeDownload(`/invoices/${i.id}/facturx`, {}, `${i.number}-factur-x.pdf`)}><FileCheck2 className="h-4 w-4 text-[#1559d6]" /></Button>
                    <Button size="icon" variant="ghost" title="XML Factur-X (CII)" data-testid={`xml-invoice-${i.id}`} onClick={() => safeDownload(`/invoices/${i.id}/xml`, {}, `${i.number}.xml`)}><FileCode2 className="h-4 w-4 text-slate-500" /></Button>
                    <Button size="icon" variant="ghost" title={i.emailed_at ? `Envoyée à ${i.emailed_to} le ${fmtDate(i.emailed_at)} — renvoyer` : i.email_error ? `Envoi échoué : ${i.email_error}` : "Envoyer par e-mail au client"} data-testid={`email-invoice-${i.id}`} onClick={() => emailInvoice(i)}><Mail className={`h-4 w-4 ${i.emailed_at ? "text-teal-600" : i.email_error ? "text-red-500" : "text-slate-500"}`} /></Button>
                    {i.status !== "payee" && (
                      <>
                        <Button size="icon" variant="ghost" title="Règlement" data-testid={`pay-invoice-${i.id}`} onClick={() => setPaying(i)}><Wallet className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" title="Marquer payée" data-testid={`markpaid-invoice-${i.id}`} onClick={() => markPaid(i)}><CheckCircle2 className="h-4 w-4 text-teal-600" /></Button>
                      </>
                    )}
                    <Button size="icon" variant="ghost" data-testid={`delete-invoice-${i.id}`} onClick={() => remove(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <PaymentDialog invoice={paying} onClose={() => setPaying(null)} onSaved={load} />
    </div>
  );
}
